import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Checkbox,
  Collapse,
  Form,
  Input,
  Spin,
  Table,
  Tooltip,
} from "antd";
import { useTranslation } from "react-i18next";
import { confirmAlert } from "react-confirm-alert";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import { ReactComponent as RupeesSign } from "../../../image/RupeesSign.svg";
import { authUserData } from "../../../utils/Helper";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import { getRequest, patchRequest } from "../../../axios";

const { Panel } = Collapse;

const getGeneralRfqMaterials = (response) =>
  response?.bid_rfq_material_details ||
  response?.bid_rfq_material_wise_detail ||
  [];

const calculateUnitRateWithGst = (
  unitRate,
  freightRate,
  gstRate,
  gstRateFreight
) => {
  const unit = Number(unitRate) || 0;
  const freight = Number(freightRate) || 0;
  const gst = Number(gstRate) || 0;
  const gstFreight = Number(gstRateFreight ?? gstRate) || 0;
  const unitWithGst = unit + unit * (gst / 100);
  const freightWithGst = freight + freight * (gstFreight / 100);
  return unitWithGst + freightWithGst;
};

const computeRowUnitRatesWithGst = (row) => {
  const gstRate = Number(row.gst_rate) || 0;
  const gstRateFreight = Number(row.gst_rate_freight ?? row.gst_rate) || 0;

  return {
    ...row,
    gst_rate: gstRate,
    gst_rate_freight: gstRateFreight,
    unit_rate_with_gst: calculateUnitRateWithGst(
      row.initial_bid_price_unit_rate,
      row.initial_bid_price_unit_rate_of_freight,
      gstRate,
      gstRateFreight
    ),
    unit_rate_with_gst_revised: calculateUnitRateWithGst(
      row.revised_bid_price_unit_rate,
      row.revised_bid_price_unit_rate_of_freight,
      gstRate,
      gstRateFreight
    ),
    unit_rate_with_gst_final: calculateUnitRateWithGst(
      row.final_bid_price_unit_rate,
      row.final_bid_price_unit_rate_of_freight,
      gstRate,
      gstRateFreight
    ),
  };
};

const mapMaterialToTableRow = (material, index) => {
  const qty = Number(material.quantity ?? material.material_total_quantity) || 0;
  const unitRate = Number(material.initial_bid_price_unit_rate) || 0;
  const freight =
    Number(
      material.initial_bid_price_unit_rate_of_freight ??
        material.unit_rate_freight
    ) || 0;

  const baseRow = {
    key: `material-${material.rfq_item_id ?? material.bid_rfq_id ?? material.id ?? index}`,
    sr_no: index + 1,
    rfq_item_id: material.rfq_item_id,
    item_description: material.item_description || "",
    approved_make: material.approved_make || "",
    remarks_from_buyer: material.remarks_from_buyer || "",
    buyer_unit_rate: Number(material.unit_rate) || 0,
    material_type_name:
      material.item_description || material.material_type_name || "",
    material_type_sub_category:
      material.approved_make || material.material_type_sub_category || "",
    material_type: material.rfq_item_id ?? material.material_type ?? index,
    material_wise_uom:
      material.unit_of_measurement || material.material_wise_uom || "",
    material_wise_uom_id: material.unit_of_measurement,
    material_total_quantity: qty,
    material_total_quantity_by_kg: material.material_total_quantity_by_kg || 0,
    gst_rate: Number(material.gst_rate ?? material.gst ?? 0) || 0,
    gst_rate_freight:
      Number(material.gst_rate_freight ?? material.gst_rate ?? material.gst ?? 0) ||
      0,
    initial_bid_price_unit_rate: unitRate,
    initial_bid_price_unit_rate_of_freight: freight,
    initial_bid_price_amount:
      Number(
        material.initial_bid_amount_without_gst ?? material.initial_bid_amount
      ) ||
      (unitRate + freight) * qty ||
      0,
    revised_bid_price_unit_rate: Number(material.revised_bid_price_unit_rate) || 0,
    revised_bid_price_unit_rate_of_freight:
      Number(material.revised_bid_price_unit_rate_of_freight) || 0,
    revised_bid_price_amount:
      Number(
        material.revised_bid_amount_without_gst ?? material.revised_bid_amount
      ) ||
      ((Number(material.revised_bid_price_unit_rate) || 0) +
        (Number(material.revised_bid_price_unit_rate_of_freight) || 0)) *
        qty,
    final_bid_price_unit_rate: Number(material.final_bid_price_unit_rate) || 0,
    final_bid_price_unit_rate_of_freight:
      Number(material.final_bid_price_unit_rate_of_freight) || 0,
    final_bid_price_amount:
      Number(
        material.final_bid_amount_without_gst ?? material.final_bid_amount
      ) ||
      ((Number(material.final_bid_price_unit_rate) || 0) +
        (Number(material.final_bid_price_unit_rate_of_freight) || 0)) *
        qty,
    initial_bid_amount_with_gst:
      Number(material.initial_bid_amount_with_gst) || 0,
    initial_bid_amount_without_gst:
      Number(
        material.initial_bid_amount_without_gst ?? material.initial_bid_amount
      ) || 0,
    revised_bid_amount_with_gst:
      Number(material.revised_bid_amount_with_gst) || 0,
    revised_bid_amount_without_gst:
      Number(
        material.revised_bid_amount_without_gst ?? material.revised_bid_amount
      ) || 0,
    final_bid_amount_with_gst: Number(material.final_bid_amount_with_gst) || 0,
    final_bid_amount_without_gst:
      Number(
        material.final_bid_amount_without_gst ?? material.final_bid_amount
      ) || 0,
    initial_bid_price_date: material.initial_bid_price_date,
    revised_bid_price_date: material.revised_bid_price_date,
    final_bid_price_date: material.final_bid_price_date,
    rfq_material_detail: material.rfq_material_detail ?? [material.rfq_item_id],
    bid_line_id: material.id,
    bid_rfq_id:
      material.bid_rfq_id ??
      material.general_bid_rfq_id ??
      (typeof material.general_bid_rfq === "object"
        ? material.general_bid_rfq?.id
        : material.general_bid_rfq),
    general_bid_rfq_id:
      material.general_bid_rfq_id ??
      (typeof material.general_bid_rfq === "object"
        ? material.general_bid_rfq?.id
        : material.general_bid_rfq),
    is_initial_bid: material.is_initial_bid,
    is_revised_bid: material.is_revised_bid,
    is_final_bid: material.is_final_bid,
    aggregate_conversion: material.aggregate_conversion,
    sand_conversion: material.sand_conversion,
    category_tree_name: material.category_tree_name,
  };

  return computeRowUnitRatesWithGst(baseRow);
};

const isMaterialRow = (row) =>
  !row?.isGrandTotal &&
  (row?.rfq_item_id != null || Boolean(row?.material_type_name));

const getMaterialRowId = (row) => row?.rfq_item_id ?? row?.material_type;

const matchesMaterialRow = (row, rowId) => {
  if (rowId == null || rowId === "") return false;
  const candidates = [row?.rfq_item_id, row?.material_type].filter(
    (value) => value != null && value !== ""
  );
  return candidates.some((value) => String(value) === String(rowId));
};

const resolveStageBidAmounts = (entry, stagePrefix) => {
  const unitRate =
    Number(entry?.[`${stagePrefix}_bid_price_unit_rate`]) || 0;
  const freightRate =
    Number(entry?.[`${stagePrefix}_bid_price_unit_rate_of_freight`]) || 0;
  const qty = Number(entry?.material_total_quantity) || 0;
  const gstRate = Number(entry?.gst_rate) || 0;
  const gstRateFreight = Number(entry?.gst_rate_freight ?? entry?.gst_rate) || 0;

  const amountWithoutGst = (unitRate + freightRate) * qty;
  const unitRateWithGst = calculateUnitRateWithGst(
    unitRate,
    freightRate,
    gstRate,
    gstRateFreight,
  );
  const amountWithGst = unitRateWithGst * qty;

  return {
    unitRate,
    freightRate,
    unitRateWithGst,
    amount: amountWithoutGst,
    amountWithoutGst,
    amountWithGst,
  };
};

const mapEntryToBidMaterialDetail = (entry, step, finalBid) => {
  const initial = resolveStageBidAmounts(entry, "initial");
  const revised = resolveStageBidAmounts(entry, "revised");
  let finalRates = resolveStageBidAmounts(entry, "final");

  // Final bid submitted from R1 uses R1 column values (revised_* fields in UI).
  if (finalBid && step === 1) {
    finalRates = { ...revised };
  }

  const detail = {
    ...(entry.bid_line_id != null ? { id: entry.bid_line_id } : {}),
    rfq_item_id: entry.rfq_item_id,
    gst_rate: entry.gst_rate,
    gst_rate_freight: entry.gst_rate_freight,
    initial_bid_price_unit_rate: initial.unitRate,
    initial_bid_price_unit_rate_of_freight: initial.freightRate,
    initial_bid_amount: initial.amountWithoutGst,
    initial_bid_amount_without_gst: initial.amountWithoutGst,
    initial_bid_amount_with_gst: initial.amountWithGst,
    revised_bid_price_unit_rate: revised.unitRate,
    revised_bid_price_unit_rate_of_freight: revised.freightRate,
    revised_bid_amount: revised.amountWithoutGst,
    revised_bid_amount_without_gst: revised.amountWithoutGst,
    revised_bid_amount_with_gst: revised.amountWithGst,
    final_bid_price_unit_rate: finalRates.unitRate,
    final_bid_price_unit_rate_of_freight: finalRates.freightRate,
    final_bid_amount: finalRates.amountWithoutGst,
    final_bid_amount_without_gst: finalRates.amountWithoutGst,
    final_bid_amount_with_gst: finalRates.amountWithGst,
    unit_rate_with_gst: initial.unitRateWithGst,
    unit_rate_with_gst_revised: revised.unitRateWithGst,
    unit_rate_with_gst_final: finalRates.unitRateWithGst,
  };

  if (step === 0) {
    return {
      ...detail,
      is_initial_bid: true,
      is_revised_bid: false,
      is_final_bid: false,
    };
  }

  if (step === 1) {
    return {
      ...detail,
      is_initial_bid: true,
      is_revised_bid: true,
      is_final_bid: finalBid,
    };
  }

  return {
    ...detail,
    is_initial_bid: true,
    is_revised_bid: true,
    is_final_bid: true,
  };
};

const buildGrandTotalRow = (rows) => {
  const materialRows = rows.filter(isMaterialRow);

  const sumAmount = (fieldUnit, fieldFreight) =>
    materialRows.reduce(
      (acc, r) =>
        acc +
        ((r[fieldUnit] || 0) + (r[fieldFreight] || 0)) *
          (r.material_total_quantity || 0),
      0
    );

  return {
    key: "grand-total",
    isGrandTotal: true,
    rowLabel: "Grand Total",
    total_initial_bid_price_unit_rate: sumAmount(
      "initial_bid_price_unit_rate",
      "initial_bid_price_unit_rate_of_freight"
    ),
    total_revised_bid_price_unit_rate: sumAmount(
      "revised_bid_price_unit_rate",
      "revised_bid_price_unit_rate_of_freight"
    ),
    total_final_bid_price_unit_rate: sumAmount(
      "final_bid_price_unit_rate",
      "final_bid_price_unit_rate_of_freight"
    ),
  };
};

const parseGeneralRfqBidResponse = (res) => {
  const payload = res?.data;
  if (payload?.success === true && payload?.data != null) {
    return payload.data;
  }
  if (
    payload?.data != null &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }
  return payload ?? null;
};

const getGeneralBidRfqStorageKey = (generalRfqId, vendorId) =>
  `general-bid-rfq-id:${generalRfqId}:${vendorId}`;

const normalizeGeneralBidRfqId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    return normalizeGeneralBidRfqId(value.id ?? value.pk ?? value.bid_rfq_id);
  }
  const numericId = Number(value);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
};

const extractGeneralBidRfqId = (data, materials = [], generalRfqId = null) => {
  if (!data) return null;

  const materialList = materials?.length
    ? materials
    : getGeneralRfqMaterials(data);

  const fromMaterials = materialList
    .map((material) =>
      normalizeGeneralBidRfqId(
        material?.general_bid_rfq_id ??
          material?.general_bid_rfq ??
          material?.bid_rfq_id ??
          material?.bid_id
      )
    )
    .find(Boolean);

  const candidates = [
    data.general_bid_rfq_id,
    data.general_bid_rfq,
    data.bid_rfq_id,
    data.bid_id,
    data.general_bid_id,
    data.general_bid_rfq_detail?.id,
    data.bid_rfq?.id ?? data.bid_rfq,
    fromMaterials,
  ];

  for (const candidate of candidates) {
    const resolvedId = normalizeGeneralBidRfqId(candidate);
    if (resolvedId) return resolvedId;
  }

  const topId = normalizeGeneralBidRfqId(data.id);
  if (
    topId &&
    generalRfqId != null &&
    String(topId) !== String(generalRfqId)
  ) {
    return topId;
  }

  return null;
};

const extractGeneralBidRfqIdFromStageResponse = (apiResponse) => {
  const payload = apiResponse?.data;
  const stageRecord =
    payload?.results?.data?.[0] ??
    (Array.isArray(payload?.data) ? payload.data[0] : payload?.data);

  return normalizeGeneralBidRfqId(
    stageRecord?.general_bid_rfq_id ??
      stageRecord?.general_bid_rfq ??
      stageRecord?.bid_rfq_id ??
      stageRecord?.id
  );
};

const VendorBidGeneralRFQ = ({ viewMode = false }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef();
  const tableDataRef = useRef([]);
  const vendorBidId = id ? atob(id) : null;
  const VendorId = authUserData()?.vendor ? authUserData()?.vendor : null;

  const [apiLoading, setApiLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rfqDetails, setRfqDetails] = useState({});
  const [rfqData, setRfqData] = useState({ open_order: false });
  const [tableData, setTableData] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isFinalBid, setIsFinalBid] = useState(false);
  const [hideR0, setHideR0] = useState(false);
  const [hideR1, setHideR1] = useState(false);
  const [buyingPatternPayload, setBuyingPatternPayload] = useState(null);
  const [showUnitRateOfCement, setShowUnitRateOfCement] = useState(false);
  const [showUnitRateOfFreight, setShowUnitRateOfFreight] = useState(false);

  const [initialBidDates, setInitialBidDates] = useState(null);
  const [revisedBidDates, setRevisedBidDates] = useState(null);
  const [finalBidDates, setFinalBidDates] = useState(null);
  const [generalBidRfqId, setGeneralBidRfqId] = useState(null);

  const currentDate = (() => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  })();

  const fetchGeneralRfqBidData = useCallback(async () => {
    if (!vendorBidId || !VendorId) {
      ErrorNotificationMsg("RFQ or vendor information is missing");
      return;
    }

    setApiLoading(true);
    try {
      const res = await getRequest(
        `general-rfqs/${vendorBidId}/get-general-rfq-city-wise/`,
        { vendor_id: VendorId }
      );
      const response = parseGeneralRfqBidResponse(res);
      if (!response) {
        ErrorNotificationMsg("Failed to load RFQ details");
        return;
      }

      const materials = getGeneralRfqMaterials(response);

      const materialRows = materials.map((m, i) => mapMaterialToTableRow(m, i));
      const resolvedBidId =
        extractGeneralBidRfqId(response, materials, vendorBidId) ||
        normalizeGeneralBidRfqId(
          sessionStorage.getItem(
            getGeneralBidRfqStorageKey(vendorBidId, VendorId)
          )
        );

      setGeneralBidRfqId(resolvedBidId);
      setTableData([...materialRows, buildGrandTotalRow(materialRows)]);
      setRfqDetails({
        ...response,
        transport_insurance:
          response.transport_insurance ?? response.transit_insurance,
      });
      setBuyingPatternPayload(
        response.bid_buying_pattern || response.buying_pattern || null
      );
      setRfqData({
        open_order: response.open_order,
        rfqamountcap: response.amount_cap ?? response.total_amount,
        rfqopendeliverydate: response.delivery_date,
        rfqdeliverystartdate: response.delivery_start_date,
        rfqdeliveryenddate: response.delivery_end_date,
        rfqduedate: response.rfq_due_date,
        rfqpricecap: response.price_cap,
      });

      if (response.is_final_bid_price) {
        setIsFinalBid(true);
      }

      const firstMaterial = materials[0];
      if (firstMaterial) {
        setInitialBidDates(firstMaterial.initial_bid_price_date || null);
        setRevisedBidDates(firstMaterial.revised_bid_price_date || null);
        setFinalBidDates(firstMaterial.final_bid_price_date || null);

        if (firstMaterial.initial_bid_price_date) {
          setCurrentStep(1);
        }
        if (firstMaterial.revised_bid_price_date) {
          setCurrentStep(2);
        }
      }

      const buyingPattern =
        response.buying_pattern || response.bid_buying_pattern;
      if (buyingPattern === "Unit Rate of Cement excluding Freight") {
        setShowUnitRateOfCement(true);
        setShowUnitRateOfFreight(true);
      } else if (buyingPattern === "Unit Rate of Cement including Freight") {
        setShowUnitRateOfCement(true);
        setShowUnitRateOfFreight(false);
      } else if (buyingPattern === "Total Unit Rate") {
        setShowUnitRateOfCement(false);
        setShowUnitRateOfFreight(true);
      } else {
        setShowUnitRateOfCement(true);
        setShowUnitRateOfFreight(true);
      }
    } catch (error) {
      console.error("Error fetching general RFQ bid:", error);
      ErrorNotificationMsg(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to load RFQ details"
      );
    } finally {
      setApiLoading(false);
    }
  }, [vendorBidId, VendorId]);

  useEffect(() => {
    fetchGeneralRfqBidData();
  }, [fetchGeneralRfqBidData]);

  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  const handleInputChange = (e, rowId, fieldName) => {
    const numericValue = parseFloat(e.target.value) || 0;

    setTableData((prevData) => {
      const materialRows = prevData.filter((r) => !r.isGrandTotal);
      const updatedMaterials = materialRows.map((record) => {
        if (!matchesMaterialRow(record, rowId)) return record;

        const freightRate =
          fieldName === "initial_bid_price_unit_rate_of_freight" ||
          fieldName === "revised_bid_price_unit_rate_of_freight" ||
          fieldName === "final_bid_price_unit_rate_of_freight"
            ? numericValue
            : fieldName === "initial_bid_price_unit_rate"
            ? record.initial_bid_price_unit_rate_of_freight
            : fieldName === "revised_bid_price_unit_rate"
            ? record.revised_bid_price_unit_rate_of_freight
            : record.final_bid_price_unit_rate_of_freight;

        const unitRate =
          fieldName === "initial_bid_price_unit_rate" ||
          fieldName === "revised_bid_price_unit_rate" ||
          fieldName === "final_bid_price_unit_rate"
            ? numericValue
            : fieldName === "initial_bid_price_unit_rate_of_freight"
            ? record.initial_bid_price_unit_rate
            : fieldName === "revised_bid_price_unit_rate_of_freight"
            ? record.revised_bid_price_unit_rate
            : record.final_bid_price_unit_rate;

        const amountField =
          fieldName === "revised_bid_price_unit_rate" ||
          fieldName === "revised_bid_price_unit_rate_of_freight"
            ? "revised_bid_price_amount"
            : fieldName === "final_bid_price_unit_rate" ||
              fieldName === "final_bid_price_unit_rate_of_freight"
            ? "final_bid_price_amount"
            : "initial_bid_price_amount";

        const nextRow = {
          ...record,
          [fieldName]: numericValue,
          [amountField]: (unitRate + (freightRate || 0)) * record.material_total_quantity,
        };

        return computeRowUnitRatesWithGst(nextRow);
      });

      return [...updatedMaterials, buildGrandTotalRow(updatedMaterials)];
    });
  };

  const buildPayload = useCallback(
    (rows) => {
      const materialRows = rows.filter(isMaterialRow);

      return {
        general_vendor: VendorId,
        is_final_bid_price: isFinalBid,
        bid_buying_pattern:
          buyingPatternPayload || "Unit Rate of Cement excluding Freight",
        bid_rfq_material_details: materialRows.map((entry) =>
          mapEntryToBidMaterialDetail(entry, currentStep, isFinalBid)
        ),
      };
    },
    [VendorId, isFinalBid, buyingPatternPayload, currentStep]
  );

  const resolveGeneralBidRfqId = useCallback(
    (rows = tableData) => {
      const fromState = normalizeGeneralBidRfqId(generalBidRfqId);
      if (fromState) return fromState;

      const materials = getGeneralRfqMaterials(rfqDetails);
      const fromResponse = extractGeneralBidRfqId(
        rfqDetails,
        materials,
        vendorBidId
      );
      if (fromResponse) return fromResponse;

      const fromStorage = normalizeGeneralBidRfqId(
        sessionStorage.getItem(
          getGeneralBidRfqStorageKey(vendorBidId, VendorId)
        )
      );
      if (fromStorage) return fromStorage;

      const materialRows = rows.filter(isMaterialRow);
      return (
        materialRows
          .map((row) =>
            normalizeGeneralBidRfqId(
              row.general_bid_rfq_id ?? row.bid_rfq_id
            )
          )
          .find(Boolean) || null
      );
    },
    [generalBidRfqId, rfqDetails, tableData, vendorBidId, VendorId]
  );

  const fetchGeneralBidRfqIdFromStage = useCallback(async () => {
    if (!vendorBidId || !VendorId) return null;

    try {
      const apiResponse = await getRequest("general-bid-rfq-po-stage/", {
        vendor_id: VendorId,
        general_rfq_id: vendorBidId,
      });
      return extractGeneralBidRfqIdFromStageResponse(apiResponse);
    } catch (error) {
      console.error("Error resolving general bid RFQ id:", error);
      return null;
    }
  }, [vendorBidId, VendorId]);

  const handleSubmit = async () => {
    const dateField =
      currentStep === 0
        ? "initial_bid_price_date"
        : currentStep === 1
        ? "revised_bid_price_date"
        : "final_bid_price_date";

    const updatedTableData = tableDataRef.current.map((item) => {
      if (isMaterialRow(item)) {
        return { ...item, [dateField]: currentDate };
      }
      return item;
    });

    setTableData(updatedTableData);

    const payload = buildPayload(updatedTableData);
    const isInitialSubmit = currentStep === 0;

    let patchBidId = resolveGeneralBidRfqId(updatedTableData);

    if (!isInitialSubmit && !patchBidId) {
      try {
        const res = await getRequest(
          `general-rfqs/${vendorBidId}/get-general-rfq-city-wise/`,
          { vendor_id: VendorId }
        );
        const refreshedResponse = parseGeneralRfqBidResponse(res);
        patchBidId = extractGeneralBidRfqId(
          refreshedResponse,
          getGeneralRfqMaterials(refreshedResponse),
          vendorBidId
        );
        if (patchBidId) {
          setGeneralBidRfqId(patchBidId);
          setRfqDetails((prev) => ({ ...prev, ...refreshedResponse }));
        }
      } catch (error) {
        console.error("Error refreshing general RFQ bid id:", error);
      }
    }

    if (!isInitialSubmit && !patchBidId) {
      patchBidId = await fetchGeneralBidRfqIdFromStage();
      if (patchBidId) {
        setGeneralBidRfqId(patchBidId);
      }
    }

    if (!isInitialSubmit && !patchBidId && initialBidDates) {
      patchBidId = normalizeGeneralBidRfqId(vendorBidId);
    }

    if (!isInitialSubmit && !patchBidId) {
      ErrorNotificationMsg(
        "Bid record not found. Please reload the page and try again."
      );
      return;
    }

    if (patchBidId) {
      sessionStorage.setItem(
        getGeneralBidRfqStorageKey(vendorBidId, VendorId),
        String(patchBidId)
      );
    }

    const submitUrl = isInitialSubmit
      ? `general-rfqs/${vendorBidId}/create-bid-rfq/`
      : `general-bid-rfq/${patchBidId}/`;

    try {
      setLoading(true);
      const response = await patchRequest(submitUrl, payload);

      if (response?.data?.success === false) {
        ErrorNotificationMsg(
          response?.data?.message ||
            response?.data?.error ||
            "Failed to submit bid"
        );
        return;
      }

      const successMessage =
        response?.data?.message || response?.data?.data?.message;

      if (isInitialSubmit) {
        const createdData = parseGeneralRfqBidResponse(response);
        const newBidId =
          extractGeneralBidRfqId(
            createdData,
            getGeneralRfqMaterials(createdData),
            vendorBidId
          ) ||
          normalizeGeneralBidRfqId(response?.data?.general_bid_rfq_id) ||
          normalizeGeneralBidRfqId(response?.data?.general_bid_rfq);
        if (newBidId) {
          setGeneralBidRfqId(newBidId);
          sessionStorage.setItem(
            getGeneralBidRfqStorageKey(vendorBidId, VendorId),
            String(newBidId)
          );
        }
      }

      if (currentStep === 0) {
        SuccessNotificationMsg(successMessage || t("bidding_of_R0"));
      } else if (currentStep === 1) {
        SuccessNotificationMsg(
          successMessage ||
            (isFinalBid ? t("Final BID Submitted") : t("bidding_of_R1"))
        );
      } else {
        SuccessNotificationMsg(
          successMessage ||
            (isFinalBid ? t("Final BID Submitted") : t("bidding_of_R"))
        );
      }

      navigate("/rfq-vendor-general-request-for-quotation/history/1");
    } catch (error) {
      console.error("Error submitting general RFQ bid:", error);
      ErrorNotificationMsg(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          "Failed to submit bid"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBidSubmission = () => {
    confirmAlert({
      title: "Confirm",
      message: isFinalBid
        ? "Are you sure that you want to submit final no regret price as you will not able to modify the BID subsequently?"
        : "Are you sure you want to Submit BID ?",
      buttons: [
        {
          label: "Yes",
          width: "90px",
          style: {
            backgroundColor: "#094e8f",
            color: "#fff",
            borderRadius: "4px",
            fontSize: "16px",
          },
          onClick: () => handleSubmit(),
        },
        {
          label: "No",
          width: "90px",
          style: {
            backgroundColor: "#d9d9d9",
            color: "#000",
            borderRadius: "4px",
            fontSize: "16px",
          },
        },
      ],
    });
  };

  const isSubmitDisabled = tableData
    .filter((i) => !i.isGrandTotal)
    .every((item) =>
      finalBidDates
        ? item.final_bid_price_unit_rate === 0
        : revisedBidDates
        ? item.revised_bid_price_unit_rate === 0
        : item.initial_bid_price_unit_rate === 0
    );

  const renderBidInputs = (record, fieldUnit, stepRequired, disabled) => {
    if (record.isGrandTotal) {
      return null;
    }

    if (viewMode || currentStep !== stepRequired) {
      return (
        <div style={{ marginLeft: "1px", marginTop: "3px" }}>
          ₹ {record[fieldUnit] ?? ""}
        </div>
      );
    }

    return (
      <Input
        name={`${fieldUnit}_${record.material_type}`}
        value={record[fieldUnit] ?? ""}
        prefix={<RupeesSign />}
        maxLength={5}
        disabled={disabled}
        onChange={(e) =>
          handleInputChange(e, getMaterialRowId(record), fieldUnit)
        }
        style={{ width: "80px" }}
      />
    );
  };

  const getColumns = () => {
    const columns = [
      {
        title: "Sr.",
        dataIndex: "sr_no",
        key: "sr_no",
        width: 50,
        fixed: "left",
        render: (text, record) => (record.isGrandTotal ? null : text),
      },
      {
        title: "Item Description",
        dataIndex: "material_type_name",
        key: "material_type_name",
        width: 160,
        fixed: "left",
        render: (text, record) =>
          record.isGrandTotal ? (
            <div className="subtotal1" style={{ textAlign: "center" }}>
              {record.rowLabel}
            </div>
          ) : (
            text
          ),
      },
      {
        title: "Qty.",
        dataIndex: "material_total_quantity",
        key: "material_total_quantity",
        align: "right",
        width: 60,
        fixed: "left",
        render: (text, record) =>
          record.isGrandTotal
            ? null
            : (
              <div style={{ marginRight: "10px" }}>
                {Number(record.material_total_quantity).toFixed(2)}
              </div>
            ),
      },
      {
        title: "UoM.",
        dataIndex: "material_wise_uom",
        key: "material_wise_uom",
        width: 100,
        fixed: "left",
        render: (text) => (text === "Cubic Meter" ? "Cu. Mtr." : text),
      },
    ];

    const addBidStage = (stageTitle, tooltipTitle, fields, stepRequired) => {
      columns.push({
        title: (
          <Tooltip color="#094E8F" placement="top" title={tooltipTitle}>
            <span>{stageTitle}</span>
          </Tooltip>
        ),
        children: [
          showUnitRateOfCement && {
            title: "Unit Rate",
            dataIndex: fields.unit,
            key: fields.unit,
            width: 120,
            render: (text, record) =>
              renderBidInputs(
                record,
                fields.unit,
                stepRequired,
                currentStep !== stepRequired
              ),
          },
          showUnitRateOfFreight && {
            title: "Unit Rate Freight",
            dataIndex: fields.freight,
            key: fields.freight,
            width: 100,
            align: "right",
            render: (text, record) => {
              if (record.isGrandTotal) return null;
              if (viewMode || currentStep !== stepRequired) {
                return (
                  <div style={{ textAlign: "end" }}>
                    ₹ {record[fields.freight] ?? 0}
                  </div>
                );
              }
              return (
                <Input
                  name={`${fields.freight}_${record.material_type}`}
                  value={record[fields.freight] ?? ""}
                  prefix={<RupeesSign />}
                  maxLength={5}
                  disabled={currentStep !== stepRequired}
                  onChange={(e) =>
                    handleInputChange(e, getMaterialRowId(record), fields.freight)
                  }
                  style={{ width: "80px" }}
                />
              );
            },
          },
          showUnitRateOfCement && {
            title: "Amount",
            dataIndex: fields.amount,
            key: fields.amount,
            width: 130,
            align: "right",
            render: (text, record) => {
              if (record.isGrandTotal) {
                const totalKey =
                  fields.unit === "initial_bid_price_unit_rate"
                    ? "total_initial_bid_price_unit_rate"
                    : fields.unit === "revised_bid_price_unit_rate"
                    ? "total_revised_bid_price_unit_rate"
                    : "total_final_bid_price_unit_rate";
                return (
                  <div className="subtotal1">
                    ₹ {record[totalKey]?.toLocaleString("en-IN") ?? "0"}
                  </div>
                );
              }
              return (
                <div className="normal-field">
                  ₹{" "}
                  {record[fields.amount]?.toLocaleString("en-IN", {
                    maximumFractionDigits: 0,
                  }) ?? "0"}
                </div>
              );
            },
          },
          showUnitRateOfCement && {
            title: "Unit Rate With GST",
            dataIndex: fields.gst,
            key: fields.gst,
            width: 130,
            align: "right",
            render: (text, record) =>
              record.isGrandTotal ? null : (
                <div className="normal-field">
                  ₹ {Number(record[fields.gst] || 0).toFixed(2)}
                </div>
              ),
          },
        ].filter(Boolean),
      });
    };

    const showFinalBidStage = viewMode
      ? Boolean(finalBidDates)
      : finalBidDates || currentStep >= 2;
    const showRevisedBidStage = viewMode
      ? Boolean(revisedBidDates) && !hideR1
      : (revisedBidDates || currentStep >= 1) && !hideR1;

    if (showFinalBidStage) {
      addBidStage(
        "R (Without GST)",
        "R - Final BID",
        {
          unit: "final_bid_price_unit_rate",
          freight: "final_bid_price_unit_rate_of_freight",
          amount: "final_bid_price_amount",
          gst: "unit_rate_with_gst_final",
        },
        2
      );
    }

    if (showRevisedBidStage) {
      addBidStage(
        "R1 (Without GST)",
        "R1 - Revised BID",
        {
          unit: "revised_bid_price_unit_rate",
          freight: "revised_bid_price_unit_rate_of_freight",
          amount: "revised_bid_price_amount",
          gst: "unit_rate_with_gst_revised",
        },
        1
      );
    }

    if (!hideR0) {
      addBidStage(
        "R0 (Without GST)",
        "R0 - Initial BID",
        {
          unit: "initial_bid_price_unit_rate",
          freight: "initial_bid_price_unit_rate_of_freight",
          amount: "initial_bid_price_amount",
          gst: "unit_rate_with_gst",
        },
        0
      );
    }

    return columns.filter(Boolean);
  };

  const columns = useMemo(() => getColumns(), [
    tableData,
    currentStep,
    hideR0,
    hideR1,
    initialBidDates,
    revisedBidDates,
    finalBidDates,
    showUnitRateOfCement,
    showUnitRateOfFreight,
    viewMode,
  ]);

  const transformedData = {
    companyName: rfqDetails.company,
    rfqnumber: rfqDetails.rfq_number,
    rfqcurrency: rfqDetails.currency_name,
    rfqpricebasis: rfqDetails.price_basis,
    rfqfreight: rfqDetails.freight,
    rfqunloading: rfqDetails.unloading,
    rfqtransportInsurance:
      rfqDetails.transport_insurance ?? rfqDetails.transit_insurance,
    rfqduedate: rfqDetails.rfq_due_date,
    rfqopendeliverydate: rfqDetails.delivery_date,
    rfqamountcap: rfqDetails.amount_cap || "N/A",
    rfqdeliverystartdate: rfqDetails.delivery_start_date || "N/A",
    rfqdeliveryenddate: rfqDetails.delivery_end_date || "N/A",
    open_order: rfqDetails.open_order,
  };

  const columns1 = [
    { title: "Company Name", dataIndex: "companyName", key: "companyName" },
    { title: "RFQ Number", dataIndex: "rfqnumber", key: "rfqnumber" },
    { title: "Currency", dataIndex: "rfqcurrency", key: "rfqcurrency" },
    { title: "Price Basis", dataIndex: "rfqpricebasis", key: "rfqpricebasis" },
    { title: "Freight", dataIndex: "rfqfreight", key: "rfqfreight" },
    {
      title: "Transport Insurance",
      dataIndex: "rfqtransportInsurance",
      key: "rfqtransportInsurance",
    },
    { title: "Unloading", dataIndex: "rfqunloading", key: "rfqunloading" },
    { title: "RFQ Due Date", dataIndex: "rfqduedate", key: "rfqduedate" },
    !rfqData.open_order && {
      title: "Delivery Date",
      dataIndex: "rfqopendeliverydate",
      key: "rfqopendeliverydate",
      render: (text, record) =>
        record.open_order ? "N/A" : record.rfqopendeliverydate,
    },
    rfqData.open_order && {
      title: "Amount Cap",
      dataIndex: "rfqamountcap",
      key: "rfqamountcap",
      render: (text, record) => (record.open_order ? text : "N/A"),
    },
    rfqData.open_order && {
      title: "Delivery Start Date",
      dataIndex: "rfqdeliverystartdate",
      key: "rfqdeliverystartdate",
      render: (text, record) => (record.open_order ? text : "N/A"),
    },
    rfqData.open_order && {
      title: "Delivery End Date",
      dataIndex: "rfqdeliveryenddate",
      key: "rfqdeliveryenddate",
      render: (text, record) => (record.open_order ? text : "N/A"),
    },
  ].filter(Boolean);

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename" style={{ whiteSpace: "nowrap" }}>
            <h3>
              {viewMode ? "View RFQ - Vendor (General)" : "BID RFQ - Vendor (General)"}
            </h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/rfq-vendor-general-request-for-quotation/history/1">
                  {t("RFQ History - Vendor")}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {viewMode ? "View RFQ - Vendor" : "BID RFQ - Vendor"}
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <ButtonField
            type="primary"
            className="CancelGradiantBtn"
            htmlType="button"
            label={t("Back")}
            onClick={() =>
              navigate("/rfq-vendor-general-request-for-quotation/history/1")
            }
          />
        </div>
      </div>

      <div className="stepscontentwrap">
        <div className="stepsContent">
          <section className="grid-sec ">
            <div className="PR-body" style={{ padding: "0" }}>
              <Collapse
                defaultActiveKey={[0]}
                className="custom-collapse-table"
              >
                <Panel header="RFQ Details" key={0}>
                  <div style={{ marginTop: "10px" }}>
                    <Table
                      className="custom-head"
                      columns={columns1}
                      dataSource={[transformedData]}
                      pagination={false}
                    />
                  </div>
                </Panel>
              </Collapse>

              <Form autoComplete="off" ref={formRef}>
                <Spin spinning={apiLoading}>
                  <Table
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    bordered
                    scroll={{ x: 1000 }}
                    sticky
                  />
                </Spin>

                {!viewMode && (
                  <div className="FormFooter">
                    {currentStep !== 2 && (
                      <Checkbox
                        checked={isFinalBid}
                        onChange={(e) => setIsFinalBid(e.target.checked)}
                      >
                        <span className="finalBid">
                          Is This Your Final Bid Price?
                        </span>
                      </Checkbox>
                    )}
                    {currentStep >= 1 && (
                      <Checkbox
                        checked={hideR0}
                        onChange={(e) => setHideR0(e.target.checked)}
                      >
                        Hide Initial BID Price - R0
                      </Checkbox>
                    )}
                    {currentStep >= 2 && (
                      <Checkbox
                        checked={hideR1}
                        onChange={(e) => setHideR1(e.target.checked)}
                      >
                        Hide BID Price - R1
                      </Checkbox>
                    )}
                  </div>
                )}
              </Form>
            </div>
          </section>
        </div>

        <div
          className="FormFooter bidftr"
          style={{ padding: "16px", margin: "0" }}
        >
          <ButtonField
            type="primary"
            className="CancelGradiantBtn"
            htmlType="button"
            label={t("Back")}
            onClick={() =>
              navigate("/rfq-vendor-general-request-for-quotation/history/1")
            }
          />
          {!viewMode && (
            <ButtonField
              type="primary"
              className="SaveGradiantBtn"
              htmlType="button"
              label="Submit BID"
              onClick={handleBidSubmission}
              loading={loading}
              disabled={isSubmitDisabled}
            />
          )}
        </div>
      </div>
    </>
  );
};

VendorBidGeneralRFQ.propTypes = {
  viewMode: PropTypes.bool,
};

export default VendorBidGeneralRFQ;
